import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, ArrowRight, Phone, Newspaper } from 'lucide-react';

// ── The Aquiflyer — Newsletter Page ──────────────────────────────────────────
// Design direction: press ticket / typewriter / newspaper aesthetic.
// Google Font "Special Elite" approximates a typewriter feel.
// Replace the Unsplash placeholder image with brand photography when available.
// TODO (Daryl/Jack): Provide final newsletter archive links, issue samples, and
//   a confirmed email platform signup embed (Mailchimp / ConvertKit / etc.)

const NewsletterPage = () => {

  return (
    <div className="min-h-screen bg-white">
      {/* Typewriter-style Google Font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Special+Elite&family=Inter:wght@400;600;700;900&display=swap"
        rel="stylesheet"
      />

      <Header />

      {/* ── Hero / Masthead ──────────────────────────────────────────────────── */}
      <section
        className="relative py-20 overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #0a2a5e 0%, #0C4288 55%, #1571BA 100%)',
        }}
      >
        {/* Decorative ruled lines (newspaper feel) */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: '#50BAED' }} />
          <div style={{ position: 'absolute', top: '8px', left: 0, right: 0, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: '#50BAED' }} />
          <div style={{ position: 'absolute', bottom: '8px', left: 0, right: 0, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          {/* Press ticket badge */}
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded mb-8"
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px dashed rgba(255,255,255,0.4)',
              fontFamily: "'Special Elite', serif",
              color: '#90D8F5',
              fontSize: '0.8rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            <Newspaper className="h-4 w-4" />
            ISSUED BY AQUIFY WATER SOLUTIONS
          </div>

          {/* Masthead */}
          <h1
            style={{
              fontFamily: "'Special Elite', serif",
              fontSize: 'clamp(2.5rem, 7vw, 5rem)',
              color: '#ffffff',
              letterSpacing: '-0.01em',
              lineHeight: 1.1,
              marginBottom: '1rem',
            }}
          >
            The Aquiflyer
          </h1>

          {/* Ruled line under masthead */}
          <div className="flex items-center gap-3 justify-center mb-6">
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.25)' }} />
            <span
              style={{
                fontFamily: "'Special Elite', serif",
                color: '#90D8F5',
                fontSize: '0.75rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              Water News &amp; Insights
            </span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.25)' }} />
          </div>

          <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Your source for water quality news, home water tips, treatment insights, and
            updates from Aquify — delivered straight to your inbox.
          </p>
        </div>
      </section>

      {/* ── What's Inside ──────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-14">
            {/* Decorative rule */}
            <div
              className="mx-auto mb-5"
              style={{
                width: '48px',
                height: '4px',
                background: 'linear-gradient(90deg, #0C4288, #1571BA)',
                borderRadius: '2px',
              }}
            />
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: "'Special Elite', serif" }}
            >
              What You'll Find Inside
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Each issue of The Aquiflyer is written by Aquify's master water specialists —
              practical, readable, and relevant to your home.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                headline: 'Water Quality Reports',
                body: 'Local water utility reports, contaminant alerts, and what they actually mean for your household.',
              },
              {
                headline: 'Treatment Tips',
                body: 'Straightforward advice on maintaining your water treatment system and maximizing its performance.',
              },
              {
                headline: 'Health & Home Protection',
                body: 'How water quality affects your pipes, appliances, skin, and family — and what to do about it.',
              },
              {
                headline: 'Industry News',
                body: 'Updates on water treatment regulations, new technologies, and what\'s changing for Long Island and Florida homeowners.',
              },
              {
                headline: 'Q&A From Our Specialists',
                body: 'Real questions from real homeowners, answered by Aquify\'s certified Master Water Specialist.',
              },
              {
                headline: 'Seasonal Reminders',
                body: 'Timely service reminders, system maintenance checklists, and things to watch for by season.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow duration-300"
                style={{ borderLeft: '4px solid #1571BA' }}
              >
                {/* Small typewriter-style issue marker */}
                <p
                  className="text-xs font-semibold uppercase tracking-widest mb-2"
                  style={{ color: '#1571BA', fontFamily: "'Special Elite', serif" }}
                >
                  ✦ Feature
                </p>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.headline}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Signup CTA ─────────────────────────────────────────────────────── */}
      <section className="py-20 bg-slate-50 border-t border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-10">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: '#E3F2FD', border: '1px solid #BFDBFE' }}
            >
              <Mail className="h-4 w-4" style={{ color: '#1571BA' }} />
              <span className="text-sm font-semibold" style={{ color: '#0C4288' }}>
                Free Subscription
              </span>
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: "'Special Elite', serif" }}
            >
              Subscribe to The Aquiflyer
            </h2>
            <p className="text-lg text-gray-600">
              Join other Long Island and South Florida homeowners who want to know more about
              their water. No spam. Unsubscribe anytime.
            </p>
          </div>

            <form
              action="https://aquifyfiltration.us20.list-manage.com/subscribe/post?u=c1a6bbade2072bd62d53742e5&id=14f3897bfa&f_id=00527ee0f0"
              method="POST"
              target="_blank"
              className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
            >
              <div className="space-y-5">
                <div>
                  <label htmlFor="mce-EMAIL" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="mce-EMAIL"
                    name="EMAIL"
                    type="email"
                    placeholder="Enter your email address"
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 text-slate-900 text-base focus:outline-none focus:ring-2"
                    style={{ focusRingColor: '#1571BA' }}
                  />
                </div>
                
                {/* Hidden tags field */}
                <input type="hidden" name="tags" value="1641010,1641011" />

                {/* Honeypot field for spam prevention */}
                <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                  <input type="text" name="b_c1a6bbade2072bd62d53742e5_14f3897bfa" tabIndex={-1} defaultValue="" />
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 text-white font-bold text-lg py-4 rounded-xl transition-all duration-300 hover:scale-105 shadow-md"
                  style={{ background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'linear-gradient(135deg, #1571BA 0%, #2A9AD8 100%)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)')}
                >
                  Subscribe
                </button>
                <p className="text-xs text-center text-gray-400">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </form>
        </div>
      </section>

      {/* ── Contact Banner ─────────────────────────────────────────────────── */}
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-lg text-gray-600 mb-3">
            Questions about your water before the next issue?
          </p>
          <p className="text-xl font-bold text-slate-900 mb-6">
            Reach out directly — we're happy to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:5167299013"
              className="inline-flex items-center gap-2 font-semibold text-lg px-8 py-3 rounded-xl border-2 transition-all duration-300"
              style={{ borderColor: '#0C4288', color: '#0C4288' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E3F2FD'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Phone className="h-5 w-5" />
              516-729-9013
            </a>
            <a
              href="mailto:info@aquify.org"
              className="inline-flex items-center gap-2 font-semibold text-lg px-8 py-3 rounded-xl border-2 transition-all duration-300"
              style={{ borderColor: '#0C4288', color: '#0C4288' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E3F2FD'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Mail className="h-5 w-5" />
              info@aquify.org
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsletterPage;
